import api from './api';

class FlightSearchService {
  // Search flights using the backend API
  async searchFlights(searchParams) {
    try {
      console.log('🔍 FlightSearchService: Searching flights with params:', searchParams);
      console.log('🔍 FlightSearchService: Trip type:', searchParams.tripType);

      // For multi-city, use POST endpoint with segments
      const isMultiCity = searchParams.tripType && searchParams.tripType.toLowerCase().includes('multi');
      if (isMultiCity && searchParams.segments) {
        return await this.searchMultiCityFlights(searchParams);
      }

      // For one-way and return flights, use GET endpoint
      const backendParams = this.transformSearchParams(searchParams);

      // Add booking_token to backend parameters if present
      if (searchParams.booking_token) {
        backendParams.booking_token = searchParams.booking_token;
      }

      // For development, if backend is not available, return mock data
      try {
        console.log('🔍 FlightSearchService: Making API call to /flights/search');
        const response = await api.get('/flights/search', { params: backendParams });

        console.log('✅ FlightSearchService: API Response:', response);

        // The response interceptor returns response.data, so check if it has success property
        if (response && (response.success || response.data)) {
          console.log('🔍 FlightSearchService: Transforming standard search results');
          return this.transformSearchResults(response);
        } else {
          throw new Error(response?.message || 'Failed to search flights');
        }
      } catch (apiError) {
        console.error('❌ FlightSearchService: API Error:', apiError);

        // If backend is not available, return mock data for development
        if (apiError.code === 'ECONNREFUSED' || apiError.message.includes('Network Error') || apiError.message.includes('ECONNREFUSED')) {
          console.log('⚠️ FlightSearchService: Backend not available, returning mock data');
          return this.getMockSearchResults(searchParams);
        }
        throw apiError;
      }
    } catch (error) {
      console.error('❌ FlightSearchService: Search error:', error);
      throw error;
    }
  }

  // Search multi-city flights using POST endpoint
  async searchMultiCityFlights(searchParams) {
    try {
      console.log('🔍 searchMultiCityFlights: Input params:', searchParams);

      const { segments, passengers, booking_token } = searchParams;

      // Validate segments
      if (!segments || segments.length < 2) {
        throw new Error('Multi-city search requires at least 2 segments');
      }

      // Transform segments to backend format
      // Backend expects airport IDs as strings
      const backendSegments = segments.map(segment => ({
        from: String(segment.from),
        to: String(segment.to),
        date: this.formatDateForAPI(segment.date)
      }));

      const requestBody = {
        segments: backendSegments,
        passengers: passengers || 1,
        booking_token
      };

      console.log('🔍 searchMultiCityFlights: Making POST request to /flights/search/multi-city');
      console.log('🔍 searchMultiCityFlights: Request body:', requestBody);

      const response = await api.post('/flights/search/multi-city', requestBody);

      console.log('✅ searchMultiCityFlights: API Response:', response);

      if (response && (response.success || response.data)) {
        return this.transformMultiCityResults(response);
      } else {
        throw new Error(response?.message || 'Failed to search multi-city flights');
      }
    } catch (error) {
      console.error('❌ searchMultiCityFlights: Error:', error);
      throw error;
    }
  }

  // Transform multi-city search results
  transformMultiCityResults(backendResponse) {
    const data = backendResponse?.data || backendResponse;

    if (!data) {
      return { success: true, segments: [], totalSegments: 0 };
    }

    // Check if response has segments (multi-city response format)
    if (data.segments && Array.isArray(data.segments)) {
      const segments = data.segments.map(segment => {
        // Get airport codes from the first flight if available
        const firstFlight = segment.flights && segment.flights.length > 0 ? segment.flights[0] : null;
        const fromCode = firstFlight?.departure?.airport_code || segment.from;
        const toCode = firstFlight?.arrival?.airport_code || segment.to;

        return {
          segmentNumber: segment.segmentNumber || segment.segment_number,
          from: fromCode,
          to: toCode,
          date: segment.date || segment.departure_date,
          flights: (segment.flights || []).map(flight => this.transformFlightData(flight)),
          totalFlights: segment.totalFlights || segment.flights?.length || 0
        };
      });

      const result = {
        success: true,
        segments,
        totalSegments: data.totalSegments || data.segments.length,
        passengers: data.passengers
      };

      console.log('✅ transformMultiCityResults: Transformed result:', result);
      return result;
    }

    // If response doesn't have segments structure, check if it's a standard response
    // and convert it to multi-city format
    if (data.outbound || data.return) {
      console.log('🔍 transformMultiCityResults: Converting standard response to multi-city format');

      // For multi-city, we might get outbound/return format, convert to segments
      const segments = [];

      if (data.outbound) {
        segments.push({
          segmentNumber: 1,
          from: data.outbound[0]?.departure?.airport_code || 'DEP',
          to: data.outbound[0]?.arrival?.airport_code || 'ARR',
          date: data.outbound[0]?.departure?.datetime,
          flights: (data.outbound || []).map(flight => this.transformFlightData(flight)),
          totalFlights: data.outbound?.length || 0
        });
      }

      if (data.return) {
        segments.push({
          segmentNumber: 2,
          from: data.return[0]?.departure?.airport_code || 'DEP',
          to: data.return[0]?.arrival?.airport_code || 'ARR',
          date: data.return[0]?.departure?.datetime,
          flights: (data.return || []).map(flight => this.transformFlightData(flight)),
          totalFlights: data.return?.length || 0
        });
      }

      const result = {
        success: true,
        segments,
        totalSegments: segments.length,
        passengers: data.passengers
      };

      console.log('✅ transformMultiCityResults: Converted result:', result);
      return result;
    }

    // If response doesn't have segments structure, return empty
    console.warn('⚠️ transformMultiCityResults: Could not find segments or outbound/return in response');
    return { success: true, segments: [], totalSegments: 0 };
  }

  // Get flight details by ID
  async getFlightDetails(flightId) {
    try {
      const response = await api.get(`/flights/details/${flightId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get airports list
  async getAirports(searchParams = {}) {
    try {
      const response = await api.get('/flights/airports', { params: searchParams });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Transform search parameters from frontend format to backend format
  transformSearchParams(frontendParams) {
    console.log('🔍 transformSearchParams: Input params:', frontendParams);

    const {
      from,
      to,
      departure,
      return: returnDate,
      passengers,
      tripType,
      nearby
    } = frontendParams;

    // Map trip types to backend format
    const routeTypeMap = {
      'One Way': 'One Way',
      'Returns': 'Round Trip',
      'Multicity': 'Multicity'
    };

    // For one-way and return flights
    const backendParams = {
      departure_airport: from,
      arrival_airport: to,
      departure_date: this.formatDateForAPI(departure),
      passengers: passengers || 1,
      route_type: routeTypeMap[tripType] || 'One Way'
    };

    // Add return date for round trip
    if (returnDate && (tripType === 'Returns' || tripType === 'Round Trip')) {
      backendParams.return_date = this.formatDateForAPI(returnDate);
    }

    // Add nearby airports flag
    if (nearby) {
      backendParams.nearby_airport = true;
    }

    console.log('✅ transformSearchParams: Backend params:', backendParams);
    return backendParams;
  }

  // Format date for API (convert from various frontend formats to YYYY-MM-DD)
  formatDateForAPI(dateString) {
    if (!dateString) return null;

    // If already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    // Try to parse various date formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }

    return date.toISOString().split('T')[0];
  }

  // Transform backend flight data to frontend format
  transformFlightData(backendFlight) {
    if (!backendFlight) return null;

    console.log('🔍 transformFlightData: Input flight:', backendFlight);
    console.log('🔍 transformFlightData: aircraft_images:', backendFlight.aircraft_images);

    const transformed = {
      id: backendFlight.id,
      flightNumber: backendFlight.flight_number,
      aircraft: {
        name: backendFlight.aircraft?.aircraftmodel?.model || 'Aircraft',
        registration: backendFlight.registration_number || backendFlight.aircraft?.registration_number,
        type: backendFlight.aircraft?.aircraftmodel?.type_designator,
        capacity: backendFlight.aircraft?.seating_capacity
      },
      departure: {
        airport: backendFlight.departure?.airport_name,
        airportCode: backendFlight.departure?.airport_code,
        airport_code: backendFlight.departure?.airport_code,
        city: backendFlight.departure?.airport_name,
        datetime: backendFlight.departure?.datetime || backendFlight.departure_date_time,
        terminal: backendFlight.terminal,
        gate: backendFlight.gate
      },
      arrival: {
        airport: backendFlight.arrival?.airport_name,
        airportCode: backendFlight.arrival?.airport_code,
        airport_code: backendFlight.arrival?.airport_code,
        city: backendFlight.arrival?.airport_name,
        datetime: backendFlight.arrival?.datetime || backendFlight.arrival_date_time
      },
      duration: this.calculateDurationMinutes(backendFlight.flight_time),
      totalSeats: backendFlight.seat_total,
      remainingSeat: backendFlight.seat_available, // Alias for legacy/hook compatibility
      availableSeats: backendFlight.remainingSeats || backendFlight.seat_available,
      price: backendFlight.seat_price
        ? Math.round(parseFloat(backendFlight.seat_price) + (parseFloat(backendFlight.seat_price) * (backendFlight.commission_fee || 0) / 100))
        : 0,
      currency: backendFlight.currency || 'USD',
      status: backendFlight.status,
      aircraft_images: backendFlight.aircraft_images || []
    };

    console.log('✅ transformFlightData: Transformed flight:', transformed);
    console.log('✅ transformFlightData: aircraft_images in result:', transformed.aircraft_images);

    return transformed;
  }

  // Calculate duration in minutes from flight_time string (e.g., "4:00")
  calculateDurationMinutes(flightTime) {
    if (!flightTime) return 0;

    const parts = flightTime.split(':');
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;

    return (hours * 60) + minutes;
  }

  // Transform search results from backend to frontend format
  transformSearchResults(backendResponse) {
    console.log('🔍 transformSearchResults: Input response:', backendResponse);

    if (!backendResponse?.data) {
      console.warn('⚠️ transformSearchResults: No data in response');
      return { success: true, flights: [], outboundFlights: [], returnFlights: [], total: 0 };
    }

    const outboundFlights = (backendResponse.data.outbound || []).map(
      flight => this.transformFlightData(flight)
    );

    const returnFlights = (backendResponse.data.return || []).map(
      flight => this.transformFlightData(flight)
    );

    const result = {
      success: true,
      outboundFlights,
      returnFlights,
      routeType: backendResponse.data.route_type,
      searchParams: backendResponse.data.search_params,
      totalOutbound: backendResponse.data.pagination?.total || outboundFlights.length,
      totalReturn: returnFlights.length
    };

    console.log('✅ transformSearchResults: Transformed result:', result);
    return result;
  }

  // Format time from datetime string
  formatTime(dateTimeString) {
    if (!dateTimeString) return '';

    try {
      const date = new Date(dateTimeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return '';
    }
  }

  // Format date from datetime string
  formatDate(dateTimeString) {
    if (!dateTimeString) return '';

    try {
      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: '2-digit'
      });
    } catch (error) {
      return '';
    }
  }

  // Calculate flight duration in hours and minutes
  calculateDuration(durationMinutes) {
    if (!durationMinutes) return '0h 0m';

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    return `${hours}h ${minutes}m`;
  }

  // Format currency
  formatCurrency(amount, currency = 'USD') {
    if (!amount) return '$0.00';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  }

  // Mock data for development when backend is not available
  getMockSearchResults(searchParams) {
    const { departure, tripType } = searchParams;

    const mockOutboundFlights = [
      {
        id: 'mock-1',
        flightNumber: 'JS101',
        aircraft: {
          name: 'Boeing 737-800',
          registration: 'N737JS',
          type: 'Commercial',
          capacity: 180
        },
        departure: {
          airport: 'John F. Kennedy International Airport',
          airportCode: 'JFK',
          city: 'New York',
          datetime: `${departure}T08:00:00Z`,
          terminal: 'T4',
          gate: 'A12'
        },
        arrival: {
          airport: 'Los Angeles International Airport',
          airportCode: 'LAX',
          city: 'Los Angeles',
          datetime: `${departure}T14:30:00Z`
        },
        duration: 390, // 6.5 hours in minutes
        totalSeats: 180,
        availableSeats: 45,
        price: 299.99,
        currency: 'USD',
        status: 'Scheduled'
      },
      {
        id: 'mock-2',
        flightNumber: 'JS205',
        aircraft: {
          name: 'Airbus A320',
          registration: 'N320JS',
          type: 'Commercial',
          capacity: 150
        },
        departure: {
          airport: 'John F. Kennedy International Airport',
          airportCode: 'JFK',
          city: 'New York',
          datetime: `${departure}T12:15:00Z`,
          terminal: 'T4',
          gate: 'B8'
        },
        arrival: {
          airport: 'Los Angeles International Airport',
          airportCode: 'LAX',
          city: 'Los Angeles',
          datetime: `${departure}T18:45:00Z`
        },
        duration: 390,
        totalSeats: 150,
        availableSeats: 23,
        price: 349.99,
        currency: 'USD',
        status: 'Scheduled'
      }
    ];

    const mockReturnFlights = tripType === 'Returns' ? [
      {
        id: 'mock-return-1',
        flightNumber: 'JS102',
        aircraft: {
          name: 'Boeing 737-800',
          registration: 'N737JS',
          type: 'Commercial',
          capacity: 180
        },
        departure: {
          airport: 'Los Angeles International Airport',
          airportCode: 'LAX',
          city: 'Los Angeles',
          datetime: `${departure}T09:00:00Z`,
          terminal: 'T1',
          gate: 'C15'
        },
        arrival: {
          airport: 'John F. Kennedy International Airport',
          airportCode: 'JFK',
          city: 'New York',
          datetime: `${departure}T17:30:00Z`
        },
        duration: 330, // 5.5 hours
        totalSeats: 180,
        availableSeats: 38,
        price: 319.99,
        currency: 'USD',
        status: 'Scheduled'
      }
    ] : [];

    return {
      success: true,
      outboundFlights: mockOutboundFlights,
      returnFlights: mockReturnFlights,
      routeType: tripType,
      searchParams: searchParams,
      totalOutbound: mockOutboundFlights.length,
      totalReturn: mockReturnFlights.length
    };
  }
}

const flightSearchService = new FlightSearchService();
export default flightSearchService;